import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Button from 'tio-reimagined/components/button';
import Card from 'tio-reimagined/components/card';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import ForgivenessEstimatorForm from 'tio-employee/components/forgiveness-estimator/forgiveness-estimator-form';
import ResultsDashboardTable from 'tio-employee/components/forgiveness-estimator/results-dashboard-table';
import Heading from 'tio-reimagined/components/heading';
import Modal from 'tio-reimagined/components/modal';
import QualifyingPaymentTracker from 'tio-employee/components/pslf/dashboard/qualifying-payment-tracker';
import Text from 'tio-reimagined/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { formatAsDollars } from 'tio-common/utils/format';
import PageHero from 'tio-reimagined/components/page-hero';
import { VStack } from 'tio-reimagined/components/layout';
import { PhHandCoins, PhArrowRight, PhWarningCircle } from 'ember-phosphor-icons';
import type { IntlService } from 'ember-intl';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type PslfFormModel from 'tio-data/models/pslf-form';
import type PslfProfileModel from 'tio-data/models/pslf-profile';
import type PersonModel from 'tio-data/models/person';
import type RecommendationModel from 'tio-data/models/recommendation';

type HeroState = 'no-nslds' | 'has-nslds' | 'annual-reminder';

interface UploadCtaCardSignature {
  Args: {
    queryParams: Record<string, string>;
  };
}

const UploadCtaCard: TOC<UploadCtaCardSignature> = <template>
  <Card @padding="lg" @roundedXl={{true}} class="flex flex-col" data-test-upload-cta-card>
    <VStack class="p-0">
      <Text @variant="muted" @size="xs" @weight="semibold" class="uppercase tracking-wider">
        {{t "pslf.reimagined.hero.upload_next_step"}}
      </Text>
      <Heading @level={{3}} @size="base">
        {{t "pslf.reimagined.hero.upload_title"}}
      </Heading>
      <Text @variant="muted" @size="sm">
        {{t "pslf.reimagined.hero.upload_description"}}
      </Text>
    </VStack>
    <Button
      class="mt-auto self-start"
      @appearance="minimal"
      @intent="primary"
      data-test-upload-data-link
      {{on
        "click"
        (transitionTo "authenticated.observability.upload" (hash queryParams=@queryParams))
      }}
    >
      {{t "pslf.reimagined.hero.upload_cta"}}
      <PhArrowRight @size={{16}} />
    </Button>
  </Card>
</template>;

export interface HeroDashboardSignature {
  Args: {
    pslfProfile: PslfProfileModel;
    pslfForms: PslfFormModel[];
    loanSummary: LoanSummary;
    person: PersonModel;
    mostRecentRecommendation?: RecommendationModel;
  };
  Element: HTMLDivElement;
}

export default class HeroDashboard extends Component<HeroDashboardSignature> {
  @service declare intl: IntlService;

  @tracked showEstimatorModal = false;
  @tracked showEstimatorResults = false;
  @tracked savedRecommendation: RecommendationModel | null = null;

  get greeting() {
    const hour = new Date().getHours();
    const firstName =
      this.args.person?.firstName?.trim() || this.intl.t('slr.reimagined.default_name');

    let timeGreeting: string;
    if (hour < 12) {
      timeGreeting = this.intl.t('slr.reimagined.good_morning');
    } else if (hour < 17) {
      timeGreeting = this.intl.t('slr.reimagined.good_afternoon');
    } else {
      timeGreeting = this.intl.t('slr.reimagined.good_evening');
    }

    return `${timeGreeting}, ${firstName}`;
  }

  get hasNsldsData() {
    const accounts = this.args.person?.accounts ?? [];
    return accounts.some((account) => account.hasNsldsPslfData);
  }

  get mostRecentCurrentEmployerForm() {
    const forms = this.args.pslfForms ?? [];
    const currentEmployerForms = forms
      .filter((f) => f.isCurrentEmployer && f.isSubmitted && f.submittedAt)
      .sort((a, b) => new Date(b.submittedAt!).getTime() - new Date(a.submittedAt!).getTime());

    return currentEmployerForms[0] ?? null;
  }

  get needsAnnualReminder() {
    const form = this.mostRecentCurrentEmployerForm;
    if (!form?.submittedAt) return false;

    const submittedDate = new Date(form.submittedAt);
    const now = new Date();
    const monthsDiff =
      (now.getFullYear() - submittedDate.getFullYear()) * 12 +
      (now.getMonth() - submittedDate.getMonth());

    return monthsDiff >= 11;
  }

  /** Days remaining until 12 months from the most recent form's submittedAt. 0 or negative means it's due now. */
  get annualReminderDaysRemaining(): number {
    const form = this.mostRecentCurrentEmployerForm;
    if (!form?.submittedAt) return 0;

    const submittedDate = new Date(form.submittedAt);
    const dueDate = new Date(submittedDate);
    dueDate.setFullYear(dueDate.getFullYear() + 1);

    const now = new Date();
    const diffMs = dueDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  get isAnnualFormDueNow() {
    return this.annualReminderDaysRemaining === 0;
  }

  get heroState(): HeroState {
    if (this.needsAnnualReminder) return 'annual-reminder';
    if (this.hasNsldsData) return 'has-nslds';
    return 'no-nslds';
  }

  get isAnnualReminder() {
    return this.heroState === 'annual-reminder';
  }

  get isNoNslds() {
    return this.heroState === 'no-nslds';
  }

  get recommendation() {
    return this.savedRecommendation ?? this.args.mostRecentRecommendation;
  }

  get recommendationForResults() {
    const recommendation = this.recommendation;

    if (!recommendation) {
      throw new Error('HeroDashboard recommendationForResults accessed without a recommendation');
    }

    return recommendation;
  }

  get hasCompletedEstimator() {
    return !!this.recommendation;
  }

  get estimatedMonthlyPayment() {
    const rec = this.recommendation;
    if (!rec) return '';
    return formatAsDollars(rec.estimatedMonthlyPayment, { cents: false });
  }

  get estimatedForgivenessAmount() {
    const rec = this.recommendation;
    if (!rec) return '';
    return formatAsDollars(rec.estimatedForgivenessAmount, { cents: false });
  }

  get estimatedForgivenessDate() {
    return this.recommendation?.estimatedForgivenessDate;
  }

  openEstimatorModal = () => {
    this.showEstimatorResults = false;
    this.showEstimatorModal = true;
  };

  openEstimatorResults = () => {
    this.showEstimatorResults = true;
    this.showEstimatorModal = true;
  };

  closeEstimatorModal = () => {
    this.showEstimatorModal = false;
    this.showEstimatorResults = false;
  };

  showFormAgain = () => {
    this.showEstimatorResults = false;
  };

  didSaveEstimator = (recommendation: RecommendationModel) => {
    this.savedRecommendation = recommendation;
    this.showEstimatorResults = true;
  };

  get hasPslfForms() {
    return (this.args.pslfForms?.length ?? 0) > 0;
  }

  get uploadQueryParams() {
    return { source: 'pslf', providers: 'nslds' };
  }

  <template>
    {{#if this.hasPslfForms}}
      <PageHero
        @gradient="brand-soft"
        class="tio-fg-primary [&_svg]:text-violet-500"
        data-test-hero-dashboard
        ...attributes
      >
        <:icon>
          <PhHandCoins @size={{20}} />
          <Text @variant="body" @size="sm" @weight="semibold" class="tio-fg-brand">
            {{this.greeting}}
          </Text>
        </:icon>
        <:default>
          <Heading @level={{1}} @size="xl" class="mb-5">
            {{t "pslf.reimagined.hero.journey_heading"}}
          </Heading>

          {{! Two-column card layout }}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            {{! ===== STATE: annual-reminder — Left: Reminder, Right: Tracker ===== }}
            {{#if this.isAnnualReminder}}
              {{! Left: Needs Attention card }}
              <Card
                @padding="lg"
                @roundedXl={{true}}
                class="tio-col"
                data-test-annual-reminder-card
              >
                <div class="flex items-center gap-1.5">
                  <PhWarningCircle @size={{16}} class="tio-fg-danger" />
                  <Text @size="sm" @weight="semibold" class="tio-fg-danger">
                    {{t "pslf.reimagined.hero.annual_reminder_badge"}}
                  </Text>
                </div>
                <Heading @level={{3}} @size="base">
                  {{t "pslf.reimagined.hero.annual_reminder_title"}}
                </Heading>
                {{#if this.isAnnualFormDueNow}}
                  <Text @variant="muted" @size="sm">
                    {{t "pslf.reimagined.hero.annual_reminder_due_now"}}
                  </Text>
                {{else}}
                  <Text @variant="muted" @size="sm">
                    {{t
                      "pslf.reimagined.hero.annual_reminder_due_soon"
                      days=this.annualReminderDaysRemaining
                    }}
                  </Text>
                {{/if}}
                <Button
                  class="mt-auto self-start"
                  @appearance="outlined"
                  @intent="primary"
                  data-test-pslf-annual-reminder-submit
                  {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                >
                  {{t "pslf.reimagined.hero.annual_reminder_cta"}}
                </Button>
              </Card>

              {{! Right: Tracker if NSLDS, otherwise Upload CTA }}
              {{#if this.hasNsldsData}}
                <QualifyingPaymentTracker @accounts={{@person.accounts}} />
              {{else}}
                <UploadCtaCard @queryParams={{this.uploadQueryParams}} />
              {{/if}}

              {{! ===== STATE: no-nslds — Left: Tracker (pending), Right: Upload CTA ===== }}
            {{else if this.isNoNslds}}
              {{! Left: Tracker in pending state }}
              <QualifyingPaymentTracker @accounts={{@person.accounts}} />

              {{! Right: Upload CTA }}
              <UploadCtaCard @queryParams={{this.uploadQueryParams}} />

              {{! ===== STATE: has-nslds — Left: Tracker, Right: Estimator CTA or Results ===== }}
            {{else}}
              {{! Left: Tracker }}
              <QualifyingPaymentTracker @accounts={{@person.accounts}} />

              {{! Right: Estimator CTA or Results }}
              <Card
                @padding="lg"
                @roundedXl={{true}}
                class="flex flex-col"
                data-test-estimator-card
              >
                {{#if this.hasCompletedEstimator}}
                  <VStack class="p-0 gap-3">
                    <Heading @level={{3}} @size="base">
                      {{t "pslf.reimagined.hero.estimator_results_heading"}}
                    </Heading>
                    <div class="space-y-2 text-sm">
                      <p>
                        <Text @weight="semibold" @size="sm">
                          {{t "pslf_dashboard.results.estimated_monthly_payment_is"}}
                        </Text>
                        <Text @size="sm" class="text-midnight">
                          {{this.estimatedMonthlyPayment}}*
                        </Text>
                      </p>
                      <p>
                        <Text @weight="semibold" @size="sm">
                          {{t "pslf_dashboard.results.estimated_loan_forgiveness_amount_is"}}
                        </Text>
                        <Text @size="sm" class="text-midnight">
                          {{this.estimatedForgivenessAmount}}
                        </Text>
                      </p>
                      <p>
                        <Text @weight="semibold" @size="sm">
                          {{t "pslf_dashboard.results.estimated_loan_forgiveness_date_is"}}
                        </Text>
                        <Text @size="sm" class="text-midnight">
                          {{this.estimatedForgivenessDate}}
                        </Text>
                      </p>
                    </div>
                    <Text @variant="muted" @size="xs">
                      {{t "recommendations_estimator.estimated_amount_forgiven_includes_interest"}}
                    </Text>
                  </VStack>
                  <Button
                    class="mt-4 self-start"
                    @appearance="outlined"
                    @intent="primary"
                    data-test-view-estimate
                    {{on "click" this.openEstimatorResults}}
                  >
                    {{t "pslf.reimagined.hero.estimator_view_results"}}
                  </Button>
                {{else}}
                  {{! Show estimator CTA }}
                  <VStack class="p-0">
                    <Text
                      @variant="muted"
                      @size="xs"
                      @weight="semibold"
                      class="uppercase tracking-wider"
                    >
                      {{t "pslf.reimagined.hero.estimator_next_step"}}
                    </Text>
                    <Heading @level={{3}} @size="base">
                      {{t "pslf.reimagined.hero.estimator_title"}}
                    </Heading>
                    <Text @variant="muted" @size="sm">
                      {{t "pslf.reimagined.hero.estimator_description"}}
                    </Text>
                  </VStack>
                  <Button
                    class="mt-4 self-start"
                    @appearance="outlined"
                    @intent="primary"
                    data-test-get-estimate-button
                    {{on "click" this.openEstimatorModal}}
                  >
                    {{t "pslf.reimagined.hero.estimator_cta"}}
                  </Button>
                {{/if}}
              </Card>
            {{/if}}
          </div>
        </:default>
      </PageHero>

    {{else}}
      <PageHero @gradient="brand-soft" class="tio-fg-primary [&_svg]:text-violet-500" ...attributes>
        <:icon>
          <PhHandCoins @size={{28}} />
          <Heading @level={{1}}>
            {{t "pslf.reimagined.hero.heading"}}
          </Heading>
        </:icon>
        <:default>
          <Text @size="lg" class="tio-fg-secondary max-w-2xl">
            {{t "pslf.reimagined.hero.description"}}
          </Text>
          <div class="mt-4 tio-row">
            <Button
              @intent="primary"
              data-test-pslf-start-new-form
              {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
            >
              {{t "pslf.reimagined.hero.get_started"}}
            </Button>
          </div>
        </:default>
      </PageHero>
    {{/if}}

    {{! Forgiveness Estimator Modal }}
    <Modal
      @isOpen={{this.showEstimatorModal}}
      @onClose={{this.closeEstimatorModal}}
      @size="2xl"
      @ariaLabel={{t "pslf.reimagined.hero.estimator_modal_title"}}
    >
      <:header>
        <Heading @level={{2}} @size="lg">
          {{#if this.showEstimatorResults}}
            {{t "pslf.reimagined.hero.estimator_results_title"}}
          {{else}}
            {{t "pslf.reimagined.hero.estimator_modal_title"}}
          {{/if}}
        </Heading>
      </:header>
      <:body>
        {{#if this.showEstimatorResults}}
          <ResultsDashboardTable
            @isCompact={{true}}
            @programType="pslf"
            @recommendation={{this.recommendationForResults}}
          />
          <div class="mt-4 flex gap-3 px-4 pb-4">
            <Button
              @appearance="outlined"
              @intent="primary"
              data-test-rerun-estimator
              {{on "click" this.showFormAgain}}
            >
              {{t "pslf.reimagined.hero.estimator_rerun"}}
            </Button>
            <Button @appearance="minimal" {{on "click" this.closeEstimatorModal}}>
              {{t "pslf.reimagined.hero.estimator_close"}}
            </Button>
          </div>
        {{else}}
          <ForgivenessEstimatorForm
            @loanSummary={{@loanSummary}}
            @application="PSLF"
            @didSave={{this.didSaveEstimator}}
          />
        {{/if}}
      </:body>
    </Modal>
  </template>
}
